import React, { useEffect, useState } from 'react';

interface Props {
  expiresAt: number | null;
  expired: boolean;
  onLoginClick: () => void;
  onExpired?: () => void;
}

const WARNING_MS = 10 * 60 * 1000;
const FONT = "'Adobe Clean', 'Source Sans Pro', 'Segoe UI', sans-serif";

export function SessionExpiryBanner({ expiresAt, expired, onLoginClick, onExpired }: Props) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!expiresAt) { setSecondsLeft(null); return; }
    let id: number;
    const tick = () => {
      const s = Math.floor((expiresAt - Date.now()) / 1000);
      setSecondsLeft(s);
      if (s <= 0) {
        onExpired?.();
        window.clearInterval(id);
      }
    };
    tick();
    id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, onExpired]);

  if (expired) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT
      }}>
        <div style={{
          background: '#fff', borderRadius: 8, padding: '36px 44px',
          maxWidth: 400, width: '90%', textAlign: 'center',
          boxShadow: '0 12px 48px rgba(0,0,0,0.25)'
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: 'linear-gradient(135deg, #eb1000 0%, #b80000 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 18px', fontSize: 22, color: '#fff'
          }}>
            🔒
          </div>
          <h2 style={{ margin: '0 0 8px', color: '#1a1a1a', fontSize: 19, fontWeight: 700, fontFamily: FONT }}>
            Session Expired
          </h2>
          <p style={{ color: '#666', margin: '0 0 28px', lineHeight: 1.6, fontSize: 13, fontFamily: FONT }}>
            Your session has expired. Please log in again to continue.
          </p>
          <button
            onClick={onLoginClick}
            style={{
              background: 'linear-gradient(135deg, #eb1000 0%, #b80000 100%)',
              color: '#fff', border: 'none', borderRadius: 5,
              padding: '10px 28px', fontSize: 14, cursor: 'pointer',
              fontWeight: 600, fontFamily: FONT, letterSpacing: '0.2px'
            }}
          >
            Log In Again
          </button>
        </div>
      </div>
    );
  }

  if (secondsLeft !== null && secondsLeft > 0 && secondsLeft <= WARNING_MS / 1000) {
    const mins = Math.floor(secondsLeft / 60);
    const secs = secondsLeft % 60;
    const label = mins > 0 ? `${mins}m ${String(secs).padStart(2, '0')}s` : `${secs}s`;
    return (
      <div
        onClick={onLoginClick}
        title="Session expiring — click to log in again"
        style={{
          position: 'fixed', top: 13, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9000, background: '#fff', color: '#eb1000',
          borderRadius: 20, padding: '5px 16px',
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, fontWeight: 700, fontFamily: FONT,
          whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
          boxShadow: '0 2px 12px rgba(0,0,0,0.22)',
          border: '1px solid rgba(235,16,0,0.15)'
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#eb1000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        <span>{label}</span>
        <span style={{ color: '#eb1000', fontWeight: 700, fontSize: 11 }}>· Log In</span>
      </div>
    );
  }

  return null;
}
